import { Component, useEffect } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AgeGate from "@/components/AgeGate";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import Confirmation from "@/pages/Confirmation";
import "@/App.css";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error) {
        const msg = String((error && error.message) || error || "");
        const isStaleChunk =
            (error && error.name === "ChunkLoadError") ||
            msg.includes("Loading chunk") ||
            msg.includes("dynamically imported module") ||
            msg.includes("Failed to fetch dynamically");
        if (isStaleChunk && !sessionStorage.getItem("dg_chunk_reloaded")) {
            sessionStorage.setItem("dg_chunk_reloaded", "1");
            window.location.reload();
        }
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink px-6 text-center">
                    <h1 className="font-display text-3xl font-extrabold text-bone">Something went sideways.</h1>
                    <p className="text-sage">Refresh the page — the greenz will be right here.</p>
                    <button
                        onClick={() => window.location.reload()}
                        data-testid="error-reload-button"
                        className="rounded-full bg-leaf px-8 py-3.5 font-bold text-ink hover:bg-leaf-bright transition-colors"
                    >
                        Reload
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollManager() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function SmoothScroll() {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let raf = requestAnimationFrame(function loop(t) {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        });
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);
    return null;
}

function NotFound() {
    return (
        <div className="container-x flex min-h-[70vh] flex-col items-center justify-center gap-5 pt-24 text-center" data-testid="not-found-page">
            <p className="eyebrow text-gold">404</p>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold text-bone">Lost in the greenz.</h1>
            <p className="text-sage">That page doesn't exist — but the menu does.</p>
            <Link to="/shop" data-testid="not-found-shop-cta" className="mt-4 rounded-full bg-leaf px-8 py-4 font-bold text-ink hover:bg-leaf-bright">
                Shop the Menu
            </Link>
        </div>
    );
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/deals" element={<Shop dealsMode />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order/:orderId" element={<Confirmation />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            <CartProvider>
                <div className="grain min-h-screen bg-ink">
                    <BrowserRouter>
                        <SmoothScroll />
                        <ScrollManager />
                        <AgeGate />
                        <Header />
                        <main>
                            <AppRoutes />
                        </main>
                        <Footer />
                        <CartDrawer />
                    </BrowserRouter>
                </div>
            </CartProvider>
        </ErrorBoundary>
    );
}
